import { formatMoment, t, type StaffKey, type StaffLanguage, type Vars } from './staffText'
import type { ReportSchedule, ReportView, ScheduleFormat, ScheduleKind } from './types'

/**
 * The pure half of the reports-by-email panel.
 *
 * Every check here mirrors one the server makes — reportmail.Recipients and
 * reportmail.Allowed — so the form can say what is wrong before a click reaches
 * the network, in the server's own reasons (the phrase keys are the server's
 * errors.VALIDATION_FAILED.* keys). The server stays the authority.
 */

export const MAX_RECIPIENTS = 20

// Something, one @, something: net/mail's shape for a bare address, which asks
// for no dot after the @ (ops@localhost is an address to it, and so to the
// server). The server's parser has the last word.
const ADDRESS = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+$/

export function parseRecipients(text: string): { addresses: string[]; problem?: StaffKey; vars?: Vars } {
  const seen = new Set<string>()
  const addresses: string[] = []
  for (const raw of text.split(/[,;\n]/)) {
    const a = raw.trim()
    if (!a) continue
    if (/[<>]/.test(a)) {
      const inner = /<([^>]+)>/.exec(a)?.[1] ?? a
      return { addresses, problem: 'errors.VALIDATION_FAILED.RECIPIENT_NAMED', vars: { address: inner } }
    }
    if (!ADDRESS.test(a)) return { addresses, problem: 'errors.VALIDATION_FAILED.RECIPIENT_INVALID', vars: { address: a } }
    const key = a.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    addresses.push(a)
  }
  if (addresses.length === 0) return { addresses, problem: 'errors.VALIDATION_FAILED.RECIPIENTS_MISSING' }
  if (addresses.length > MAX_RECIPIENTS) {
    return { addresses, problem: 'errors.VALIDATION_FAILED.RECIPIENTS_TOO_MANY', vars: { limit: MAX_RECIPIENTS } }
  }
  return { addresses }
}

/** The addresses the owner's domains do not allow: an exact match after the @. */
export function outsideDomains(addresses: readonly string[], domains: readonly string[]): string[] {
  if (domains.length === 0) return []
  const allowed = new Set(domains.map((d) => d.trim().toLowerCase()))
  return addresses.filter((a) => !allowed.has(a.slice(a.lastIndexOf('@') + 1).toLowerCase()))
}

export function minuteOf(clock: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(clock.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

export function clockOf(minute: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`
}

/**
 * The minute of the day a branch's letter really goes: the later of the chosen
 * minute and the branch's rollover, since a day's figures are not complete
 * before the day ends. The server's reportmail.sendMoment, to the minute. A
 * network letter keeps midnight as its day's end, so its rollover is 0.
 */
export function sendsAt(sendMinute: number, rolloverMinute: number): number {
  const rollover = ((rolloverMinute % 1440) + 1440) % 1440
  return Math.max(sendMinute, rollover)
}

export function describeSchedule(
  s: Pick<ReportSchedule, 'frequency' | 'sendMinute' | 'branchId'>,
  lang: StaffLanguage,
  rolloverMinute = 0,
): string {
  const branch = !!s.branchId
  const minute = branch ? sendsAt(s.sendMinute, rolloverMinute) : s.sendMinute
  const when = t(`reportMail.describe.${s.frequency}` as StaffKey, lang, { time: clockOf(minute) })
  return `${when}, ${t(branch ? 'reportMail.zone.branch' : 'reportMail.zone.network', lang)}`
}

/** The hint under the form's time: whose clock it is, and — when the branch's day
 *  ends after the minute chosen — the time the letter will really go. */
export function timeHint(branch: boolean, sendMinute: number | null, rolloverMinute: number, lang: StaffLanguage): string {
  if (!branch) return t('reportMail.timeHint.network', lang)
  const hint = t('reportMail.timeHint.branch', lang)
  if (sendMinute === null || sendsAt(sendMinute, rolloverMinute) === sendMinute) return hint
  return `${hint} ${t('reportMail.timeHint.later', lang, { time: clockOf(sendsAt(sendMinute, rolloverMinute)) })}`
}

export interface Banner {
  key: StaffKey
  vars?: Vars
  tone: 'warn' | 'info'
}

/**
 * What the panel must say above a schedule, most urgent first. A missing mail
 * server comes before a failed letter, because until it is fixed every letter
 * will fail and the relay's last words are not the reason.
 */
export function scheduleBanner(s: ReportSchedule): Banner | null {
  switch (s.mailProblem) {
    case 'none':
      return { key: 'reportMail.banner.noServer', tone: 'warn' }
    case 'off':
      return { key: 'reportMail.banner.serverOff', tone: 'warn' }
    case 'unreadable':
      return { key: 'reportMail.banner.serverUnreadable', tone: 'warn' }
  }
  switch (s.problem) {
    case 'unlicensed':
      return { key: 'reportMail.banner.unlicensed', tone: 'info' }
    case 'unassigned':
      return { key: 'reportMail.banner.unassigned', tone: 'warn' }
    case 'no_recipient_allowed':
      return { key: 'reportMail.banner.noRecipient', tone: 'warn' }
    case 'branch_inactive':
      return { key: 'reportMail.banner.branchInactive', tone: 'info' }
    case 'not_built':
      return { key: 'reportMail.banner.notBuilt', tone: 'warn' }
    case 'view_unavailable':
      return { key: s.viewOwnerLeft ? 'reportMail.banner.viewOwnerLeft' : 'reportMail.banner.viewUnavailable', tone: 'warn' }
  }
  if (s.last?.status === 'failed') {
    // Not the period: the last letter may be one Send now queued, whose period
    // is not the schedule's lastPeriodFrom.
    return { key: 'reportMail.banner.failed', vars: { error: s.last.lastError ?? '—' }, tone: 'warn' }
  }
  // A retry is a letter an attempt already failed for: one being sent for the
  // first time (claimed, so attempts is already 1) is no retry.
  if (s.last?.status === 'pending' && s.last.attempts > 0 && s.last.lastError) {
    return { key: 'reportMail.banner.retrying', vars: { n: s.last.attempts, error: s.last.lastError ?? '—' }, tone: 'info' }
  }
  return null
}

/** What became of the schedule's last letter, in one line. "Delivered" says
 *  where delivery ends: at the mail server, which is all SMTP can know. */
export function lastLetter(s: ReportSchedule, lang: StaffLanguage): string {
  switch (s.last?.status) {
    case 'delivered':
      return t('reportMail.last.delivered', lang, {
        when: s.last.deliveredAt ? formatMoment(s.last.deliveredAt, lang, { date: true, time: true }) : '—',
      })
    case 'pending':
      return t('reportMail.last.pending', lang)
    case 'failed':
      return t('reportMail.last.failed', lang)
    default:
      return t('reportMail.last.none', lang)
  }
}

/** The files a letter may carry, as the server allows them (api formatAllowed):
 *  a branch's summary CSV or PDF, the network's CSV, a saved view CSV or Excel. */
export function formatsFor(kind: ScheduleKind, branchId: string): ScheduleFormat[] {
  if (kind === 'summary') return branchId ? ['csv', 'pdf'] : ['csv']
  return ['csv', 'xlsx']
}

/** The saved views a letter of this kind may send: reports for a saved view, Tickets views for tickets. */
export function viewsFor(kind: ScheduleKind, views: readonly ReportView[]): ReportView[] {
  if (kind === 'summary') return []
  return views.filter((v) => (kind === 'view' ? v.kind === 'figures' || v.kind === 'pivot' : v.kind === 'tickets'))
}

/** Where a letter of this kind may go: '' is every branch, offered for the
 *  network's summary (with Analytics), for tickets and for reports. */
export function everyBranchFor(kind: ScheduleKind, canNetwork: boolean, canAllBranches: boolean): boolean {
  if (kind === 'summary') return canNetwork
  return canAllBranches
}

/** A draft moved to another kind or place keeps what still fits: the format if allowed, the view if listed. */
export function fitDraft<D extends { kind: ScheduleKind; branchId: string; format: ScheduleFormat; viewId: string }>(d: D, views: readonly ReportView[]): D {
  const formats = formatsFor(d.kind, d.branchId)
  const listed = viewsFor(d.kind, views)
  return {
    ...d,
    format: formats.includes(d.format) ? d.format : formats[0]!,
    viewId: listed.some((v) => v.id === d.viewId) ? d.viewId : (listed[0]?.id ?? ''),
  }
}

/** The branches a letter covers by id; none is every branch. */
export function letterBranches(s: Pick<ReportSchedule, 'branchId' | 'branchIds'>): string[] {
  if (s.branchIds?.length) return s.branchIds
  return s.branchId ? [s.branchId] : []
}

/** The ticks a letter opens with: every listed branch for a letter to every branch. */
export function pickedOf(s: Pick<ReportSchedule, 'branchId' | 'branchIds'>, listed: readonly string[]): string[] {
  const ids = letterBranches(s)
  return ids.length ? ids : [...listed]
}

/** One branch ticked or unticked, kept in the listed order. */
export function toggleBranch(picked: readonly string[], id: string, listed: readonly string[]): string[] {
  const on = picked.includes(id)
  return listed.filter((b) => (b === id ? !on : picked.includes(b)))
}

/** All branches: ticks every one, or clears them when all were ticked. */
export function toggleAllBranches(picked: readonly string[], listed: readonly string[]): string[] {
  return listed.every((b) => picked.includes(b)) ? [] : [...listed]
}

/** What a save sends: none for every branch where that is allowed, else the ticked ones. */
export function branchesToSend(picked: readonly string[], listed: readonly string[], everyAllowed: boolean): string[] {
  const ticked = listed.filter((b) => picked.includes(b))
  return everyAllowed && listed.length > 1 && ticked.length === listed.length ? [] : ticked
}
